/**
 * Wartungspläne-Tab (Jul-2026, User-Wunsch) — Hersteller-Wartungspläne zentral
 * pflegen. Ordner-/Mehrfach-Upload: jede PDF/TXT wird per KI in einen
 * strukturierten Plan umgewandelt (prüfen & verifizieren).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Search, Upload, Trash2, CheckCircle2, Loader2, FolderOpen, ChevronDown, Wrench } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const fmtKm = (v) => (v ? `${Number(v).toLocaleString("de-DE")} km` : null);

export default function MaintenancePlansTab() {
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [openId, setOpenId] = useState(null);
    const fileRef = useRef(null);
    const folderRef = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/maintenance-plans", { params: { q } });
            setItems(r.data.items || []);
        } catch { toast.error("Laden fehlgeschlagen"); }
        finally { setLoading(false); }
    }, [q]);
    useEffect(() => { load(); }, [load]);

    const upload = async (fileList) => {
        const files = Array.from(fileList || []).filter((f) =>
            /\.(pdf|txt|md|csv)$/i.test(f.name));
        if (!files.length) { toast.error("Keine PDF/TXT/MD/CSV-Dateien gefunden."); return; }
        if (files.length > 40) { toast.error("Max. 40 Dateien pro Upload."); return; }
        setUploading(true);
        try {
            const fd = new FormData();
            files.forEach((f) => fd.append("files", f));
            const { data } = await api.post("/admin/maintenance-plans/upload", fd, { timeout: 600000 });
            toast.success(`${data.ok_count} Plan/Pläne übernommen${data.fail_count ? ` · ${data.fail_count} fehlgeschlagen` : ""}`);
            (data.results || []).filter((r) => !r.ok).slice(0, 3).forEach((r) =>
                toast.error(`${r.file}: ${r.error}`));
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Upload fehlgeschlagen"); }
        finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
            if (folderRef.current) folderRef.current.value = "";
        }
    };

    const verify = async (p) => {
        try {
            await api.patch(`/admin/maintenance-plans/${p.id}`, { verified: true });
            toast.success(`${p.manufacturer} ${p.model} verifiziert`);
            load();
        } catch { toast.error("Verifizieren fehlgeschlagen"); }
    };

    const remove = async (p) => {
        if (!window.confirm(`Wartungsplan „${p.manufacturer} ${p.model}" löschen?`)) return;
        try {
            await api.delete(`/admin/maintenance-plans/${p.id}`);
            setItems((l) => l.filter((x) => x.id !== p.id));
        } catch { toast.error("Löschen fehlgeschlagen"); }
    };

    return (
        <div data-testid="maintenance-plans-tab">
            <div className="flex flex-wrap items-center gap-2 mb-4">
                <div className="relative flex-1 min-w-[220px] max-w-md">
                    <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suche: Hersteller, Modell…" className="pl-8 rounded-sm" data-testid="mp-search" />
                </div>
                <span className="text-xs text-gray-500">{items.length} Pläne</span>
                <input ref={fileRef} type="file" multiple accept=".pdf,.txt,.md,.csv" className="hidden"
                    onChange={(e) => upload(e.target.files)} data-testid="mp-file-input" />
                <input ref={folderRef} type="file" webkitdirectory="" directory="" multiple className="hidden"
                    onChange={(e) => upload(e.target.files)} data-testid="mp-folder-input" />
                <Button variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()}
                    className="rounded-sm text-xs h-9 ml-auto" data-testid="mp-upload-files-btn">
                    {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
                    Dateien hochladen
                </Button>
                <Button disabled={uploading} onClick={() => folderRef.current?.click()}
                    className="rounded-sm text-xs h-9 bg-[#111827] hover:bg-black text-white" data-testid="mp-upload-folder-btn">
                    {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FolderOpen className="w-4 h-4 mr-1.5" />}
                    Ganzen Ordner hochladen
                </Button>
            </div>
            <div className="mb-4 text-[11px] text-amber-800 bg-amber-50 border border-amber-300 rounded-sm px-3 py-2">
                PDFs/TXTs (auch komplette Ordner) hochladen — die KI erstellt daraus strukturierte Wartungspläne je Hersteller + Modell.
                KI-Ergebnisse bitte prüfen und mit „✓ Geprüft" bestätigen. Die Pläne steuern den Wartungs-Hinweis in der Werkstatt-Auftragsanlage.
            </div>
            {loading ? (
                <div className="text-center py-12 text-gray-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
            ) : items.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded-sm p-10 text-center text-gray-500" data-testid="mp-empty">
                    Noch keine Wartungspläne — lade Hersteller-Unterlagen (PDF) hoch, die KI übernimmt die Struktur.
                </div>
            ) : (
                <div className="space-y-2">
                    {items.map((p) => (
                        <div key={p.id} className="bg-white border border-gray-200 rounded-sm" data-testid={`mp-row-${p.id}`}>
                            <button type="button" onClick={() => setOpenId(openId === p.id ? null : p.id)}
                                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50">
                                <Wrench className="w-4 h-4 text-[#FFCC00] shrink-0" />
                                <span className="flex-1 min-w-0">
                                    <b className="text-sm">{p.manufacturer} {p.model}</b>
                                    <span className="ml-2 text-[11px] text-gray-500">{(p.intervals || []).length} Intervalle{p.source_file ? ` · ${p.source_file}` : ""}</span>
                                </span>
                                {p.verified ? (
                                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /> verifiziert</span>
                                ) : (
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-blue-600">KI — bitte prüfen</span>
                                )}
                                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${openId === p.id ? "rotate-180" : ""}`} />
                            </button>
                            {openId === p.id && (
                                <div className="border-t border-gray-100 px-4 py-3">
                                    <table className="w-full text-xs">
                                        <thead><tr className="text-left text-[10px] uppercase tracking-wider text-gray-500">
                                            <th className="py-1 pr-3">Wartung</th><th className="py-1 pr-3">Intervall</th><th className="py-1">Arbeiten</th>
                                        </tr></thead>
                                        <tbody>
                                            {(p.intervals || []).map((it, i) => (
                                                <tr key={i} className="border-t border-gray-100 align-top">
                                                    <td className="py-1.5 pr-3 font-bold">{it.title}</td>
                                                    <td className="py-1.5 pr-3 whitespace-nowrap font-mono-num">
                                                        {[fmtKm(it.km), it.months ? `${it.months} Mon.` : null].filter(Boolean).join(" / ") || "—"}
                                                        {it.repeat ? " (wiederkehrend)" : ""}
                                                    </td>
                                                    <td className="py-1.5 text-gray-600">{(it.tasks || []).join(", ") || "—"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {p.notes && <div className="mt-2 text-[11px] text-gray-500">{p.notes}</div>}
                                    <div className="mt-3 flex gap-2">
                                        {!p.verified && (
                                            <Button size="sm" variant="outline" onClick={() => verify(p)}
                                                className="rounded-sm h-8 text-emerald-700 border-emerald-300 hover:bg-emerald-50" data-testid={`mp-verify-${p.id}`}>
                                                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Geprüft
                                            </Button>
                                        )}
                                        <Button size="sm" variant="outline" onClick={() => remove(p)}
                                            className="rounded-sm h-8 text-red-600 border-red-300 hover:bg-red-50" data-testid={`mp-delete-${p.id}`}>
                                            <Trash2 className="w-3.5 h-3.5 mr-1" /> Löschen
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
